import {
  CRM_PRO_VIEW_ALL_LEAD,
  CRM_PRO_VIEW_ASSIGNED_LEAD,
  CRM_VIEW_ALL_LEAD,
  CRM_VIEW_ASSIGNED_LEAD,
  DASHBOARD_MODULE,
  DASHBOARD_PRO_MODULE,
  REPORT_MODULE,
  VIEW_ACCOUNT,
  VIEW_ALL_TASK,
  VIEW_ASSIGNED_TASK,
  VIEW_CLIENT,
  VIEW_CUSTOM_INVOICE,
  VIEW_EMPLOYEE,
  VIEW_EXPENSE,
  VIEW_GOVERNMENT_INVOICE,
  VIEW_OFFICE_DASHBOARD,
  VIEW_PERFORMANCE_GUARANTEE,
  VIEW_PROJECT,
  VIEW_ROLE,
  VIEW_SUBCONTRACTOR,
  VIEW_TOOL,
  VIEW_USER,
  VIEW_VENDOR,
  VIEW_WORKER,
} from "src/constants";
import { ACCOUNT_ROUTES } from "src/pages/account/constants";
import { ADMINISTRATION_ROUTES } from "src/pages/administration/constants";
import { AUTH_ROUTES } from "src/pages/auth/constants";
import { CLIENT_ROUTES } from "src/pages/client/constants";
import { CRM_PRO_ROUTES } from "src/pages/crm-pro/constants";
import { CRM_ROUTES } from "src/pages/crm/constants";
import { DASHBOARD_ROUTES } from "src/pages/dashboard/constants";
import { EMPLOYEE_ROUTES } from "src/pages/employee/constants";
import { EXPENSE_ROUTES } from "src/pages/expense/constants";
import { INVOICES_ROUTES } from "src/pages/invoices/constants";
import { OFFICE_ROUTES } from "src/pages/office/constants";
import { PERFORMANCE_GUARANTEES_ROUTES } from "src/pages/performanceGuarantees/constants";
import { PROJECT_ROUTES } from "src/pages/project/constants";
import { REPORT_ROUTES } from "src/pages/report/constants";
import { SUBCONTRACTORS_ROUTES } from "src/pages/subcontractors/constants";
import { TASK_ROUTES } from "src/pages/task/constants";
import { TOOLS_ROUTES } from "src/pages/tools/constants";
import { VENDOR_ROUTES } from "src/pages/vendor/constants";
// WORKER_ROUTES is the one exception: `worker/constants.tsx` imports `AccountService` directly from the
// `services` barrel (for a table's live data), which is a different, unrelated cycle back into
// `services/api.config.ts` — removing `ProtectedRoute` from the `utils` barrel doesn't touch that path. Its
// routes stay in the dependency-free `worker/routes.constants.ts`; see that file for details.
import { WORKER_ROUTES } from "src/pages/worker/routes.constants";
// Dependency-free by design, so it can't re-enter `services/api.config.ts` from here.
import { clearTableState } from "src/utils/tableStateStorage";

const LOGIN_PATH = AUTH_ROUTES.login;
const FORGOT_PASSWORD_PATH = AUTH_ROUTES.forgotPassword;

const normalizeHashPath = (hashPath = "") => {
  let path = String(hashPath || "").trim();

  if (!path) return "";
  if (path.startsWith("#")) path = path.slice(1);
  if (!path.startsWith("/")) path = `/${path}`;

  return path;
};

export const sanitizeReturnPath = (returnTo) => {
  if (typeof returnTo !== "string") return null;

  const raw = returnTo.trim();
  if (!raw) return null;

  const normalized = normalizeHashPath(raw);
  if (!normalized) return null;

  const pathPart = normalized.split("?")[0].toLowerCase();

  if (normalized.startsWith("//")) return null;
  if (normalized.includes("://")) return null;
  if (pathPart === LOGIN_PATH || pathPart.startsWith(`${LOGIN_PATH}/`)) return null;
  if (pathPart === FORGOT_PASSWORD_PATH || pathPart.startsWith(`${FORGOT_PASSWORD_PATH}/`)) return null;

  return normalized;
};

export const buildLoginRedirectWithReturn = (currentHashPath = "") => {
  const normalizedCurrentPath = sanitizeReturnPath(currentHashPath);
  if (!normalizedCurrentPath) return LOGIN_PATH;

  const params = new URLSearchParams({ returnTo: normalizedCurrentPath });
  return `${LOGIN_PATH}?${params.toString()}`;
};

export const handleUnAuthorized = (currentHashPath = "") => {
  if (window.location.hash.startsWith("#/login")) {
    return;
  }

  const loginPath = buildLoginRedirectWithReturn(currentHashPath || window.location.hash);
  // Remembered table filters can hold names and other search text; the next session starts clean.
  clearTableState();
  window.location.hash = loginPath;
};

/**
 * @param {string|string[]} requiredPermissions - Required permission(s)
 * @param {'AND'|'OR'} logic - Logic to apply if multiple permissions are passed
 * @param {string|string[]} userPermissions - User permissions
 * @returns {boolean}
 */
export const hasPermission = (requiredPermissions, userPermissions, logic = "OR") => {
  if (requiredPermissions) {
    const permissions = Array.isArray(requiredPermissions) ? requiredPermissions : [requiredPermissions];

    if (logic === "AND") {
      return permissions.every((permission) => userPermissions.includes(permission));
    }
    return permissions.some((permission) => userPermissions.includes(permission));
  }

  return false;
};

/**
 * @param {string|string[]} requiredType - Required type(s)
 * @param {string} actualType - User type
 * @returns {boolean}
 */
export const isUserType = (requiredType, actualType) => {
  if (!requiredType) return true;
  return Array.isArray(requiredType) ? requiredType.includes(actualType) : requiredType === actualType;
};

export const MODULE_ROUTE_MAP = {
  [DASHBOARD_MODULE]: DASHBOARD_ROUTES.root,
  [DASHBOARD_PRO_MODULE]: DASHBOARD_ROUTES.root,
  [CRM_VIEW_ALL_LEAD]: CRM_ROUTES.root,
  [CRM_VIEW_ASSIGNED_LEAD]: CRM_ROUTES.root,
  [CRM_PRO_VIEW_ALL_LEAD]: CRM_PRO_ROUTES.root,
  [CRM_PRO_VIEW_ASSIGNED_LEAD]: CRM_PRO_ROUTES.root,
  [VIEW_PROJECT]: PROJECT_ROUTES.root,
  [VIEW_OFFICE_DASHBOARD]: OFFICE_ROUTES.overview,
  [VIEW_WORKER]: WORKER_ROUTES.root,
  [VIEW_VENDOR]: VENDOR_ROUTES.root,
  [VIEW_EXPENSE]: EXPENSE_ROUTES.root,
  [VIEW_SUBCONTRACTOR]: SUBCONTRACTORS_ROUTES.root,
  [VIEW_CLIENT]: CLIENT_ROUTES.root,
  [VIEW_EMPLOYEE]: EMPLOYEE_ROUTES.root,
  [VIEW_USER]: ADMINISTRATION_ROUTES.root,
  [VIEW_ROLE]: ADMINISTRATION_ROUTES.root,
  [VIEW_ACCOUNT]: ACCOUNT_ROUTES.root,
  [VIEW_ALL_TASK]: TASK_ROUTES.root,
  [VIEW_ASSIGNED_TASK]: TASK_ROUTES.root,
  [VIEW_TOOL]: TOOLS_ROUTES.root,
  [VIEW_PERFORMANCE_GUARANTEE]: PERFORMANCE_GUARANTEES_ROUTES.root,
  [VIEW_CUSTOM_INVOICE]: INVOICES_ROUTES.root,
  [VIEW_GOVERNMENT_INVOICE]: INVOICES_ROUTES.root,
  [REPORT_MODULE]: REPORT_ROUTES.root,
};
